import { GB_KW, NI_KW } from "../../../constants/generalConstants"

export const MANUFACTURER_ID_URL_PARAM = "manufacturer_id"
export const MANUFACTURER_OPTION_ID = "ManufacturerID"
export const MANUFACTURER_REF_KEY = "deviceManufacturers"

export const CATEGORY_ID_URL_PARAM = "category_id"
export const CATEGORY_OPTION_ID = "DeviceCategoryID"
export const CATEGORY_REF_KEY = "deviceCategories"

export const DEVICE_TYPE_ID_URL_PARAM = "device_type_id"
export const DEVICE_TYPE_OPTION_ID = "DeviceTypeID"
export const DEVICE_TYPE_REF_KEY = "deviceTypes"

export const COMPLIANCE_STATUS_ID_URL_PARAM = "compliance_status_id"
export const COMPLIANCE_STATUS_OPTION_ID = "ComplianceStatusID"
export const COMPLIANCE_STATUS_REF_KEY = "complianceStatuses"

export const PHASE_ID_URL_PARAM = "status"
export const PHASE_OPTION_ID = "PhaseID"
export const PHASE_REF_KEY = "phase_id"

export const JURISDICTION_OPTION_ID = "jurisdictionId"
export const JURISDICTION_REF_KEY = "jurisdiction"
export const JURISDICTION_ID_URL_PARAM = JURISDICTION_REF_KEY

export type TGenFilterRefKeys =
    | typeof MANUFACTURER_REF_KEY
    | typeof CATEGORY_REF_KEY
    | typeof DEVICE_TYPE_REF_KEY
    | typeof COMPLIANCE_STATUS_REF_KEY
    | typeof PHASE_REF_KEY
    | typeof JURISDICTION_REF_KEY

// Types for each set of options
export type TPhasesRef = { [PHASE_OPTION_ID]: number; Phase: string }
export type TDeviceTypes = {
    [DEVICE_TYPE_OPTION_ID]: number
    DeviceType: string
}
export type TDeviceCatRef = {
    [CATEGORY_OPTION_ID]: number
    DeviceCategory: string
}
export type TComplianceStatusRef = {
    ComplianceStatusID: number
    StatusShort: string
}
export type TManufacturerRef = {
    [MANUFACTURER_OPTION_ID]: number
    Manufacturer: string
}
export type TJurisdiction = {
    [JURISDICTION_OPTION_ID]: string
    [JURISDICTION_REF_KEY]: string
}

// Raw API input type
type TGenRefInput = {
    compliance_statuses: TComplianceStatusRef[]
    device_categories: TDeviceCatRef[]
    device_manufacturers: TManufacturerRef[]
    device_phases: TPhasesRef[]
    device_types: TDeviceTypes[]
}

// Mapper Output type
export type TGenRefOutput = {
    complianceStatuses: TComplianceStatusRef[]
    deviceCategories: TDeviceCatRef[]
    deviceManufacturers: TManufacturerRef[]
    phase_id: TPhasesRef[]
    deviceTypes: TDeviceTypes[]
    jurisdiction: TJurisdiction[]
}

// Overall output type
type TGenFilterReferenceMapper = {
    map: (response: unknown) => TGenRefOutput
    idMappings: Record<
        TGenFilterRefKeys,
        {
            searchParam: string
            optionId: string
            referenceDataKey: string
        }
    >
}
export const genFilterReferenceMapper: TGenFilterReferenceMapper = {
    /**
     * Maps API response into UI data model and replaces missing arrays. Appends
     * in static option jurisdiction. This is for generation search reference
     * data
     */
    map: (response: unknown) => ({
        [COMPLIANCE_STATUS_REF_KEY]:
            (response as TGenRefInput)?.compliance_statuses || [],
        [CATEGORY_REF_KEY]: (response as TGenRefInput)?.device_categories || [],
        [MANUFACTURER_REF_KEY]:
            (response as TGenRefInput)?.device_manufacturers || [],
        [PHASE_REF_KEY]: (response as TGenRefInput)?.device_phases || [],
        [DEVICE_TYPE_REF_KEY]: (response as TGenRefInput)?.device_types || [],
        [JURISDICTION_REF_KEY]: [
            { [JURISDICTION_OPTION_ID]: GB_KW, [JURISDICTION_REF_KEY]: GB_KW },
            { [JURISDICTION_OPTION_ID]: NI_KW, [JURISDICTION_REF_KEY]: NI_KW },
        ],
    }),
    /**
     * Provides mappings between identifier in URL search params, reference
     * data option provided by APIs and key in UI data model for accessing
     * reference data.
     */
    idMappings: {
        [MANUFACTURER_REF_KEY]: {
            searchParam: MANUFACTURER_ID_URL_PARAM,
            optionId: MANUFACTURER_OPTION_ID,
            referenceDataKey: MANUFACTURER_REF_KEY,
        },
        [CATEGORY_REF_KEY]: {
            searchParam: CATEGORY_ID_URL_PARAM,
            optionId: CATEGORY_OPTION_ID,
            referenceDataKey: CATEGORY_REF_KEY,
        },
        [DEVICE_TYPE_REF_KEY]: {
            searchParam: DEVICE_TYPE_ID_URL_PARAM,
            optionId: DEVICE_TYPE_OPTION_ID,
            referenceDataKey: DEVICE_TYPE_REF_KEY,
        },

        [COMPLIANCE_STATUS_REF_KEY]: {
            searchParam: COMPLIANCE_STATUS_ID_URL_PARAM,
            optionId: COMPLIANCE_STATUS_OPTION_ID,
            referenceDataKey: COMPLIANCE_STATUS_REF_KEY,
        },
        [PHASE_REF_KEY]: {
            searchParam: PHASE_REF_KEY,
            optionId: PHASE_OPTION_ID,
            referenceDataKey: PHASE_REF_KEY,
        },
        [JURISDICTION_REF_KEY]: {
            searchParam: JURISDICTION_ID_URL_PARAM,
            optionId: JURISDICTION_OPTION_ID,
            referenceDataKey: JURISDICTION_REF_KEY,
        },
    },
}
